import { createAsyncThunk, createSlice, isAnyOf } from "@reduxjs/toolkit";
import api from "../../utils/api";
import { API_ENDPOINTS, METHOD_TYPE } from "../../utils/apiUrls";

const initialState = {
    isLoading: false,
    saveLoading: false,
    error: null,
    countryList: [],
    currencyList: [],
    countryAssignments: [],
    taxProfiles: [],
    fxRates: [],
    pricingSlabs: [],
    serviceCatalog: [],
    plan: null,
    quotePreview: null,
    formTemplates: [],
    onboardingPlans: [],
    currentOnboardingPlan: null,
    lastSuccessMessage: '',
};

const SELF_SERVICE_PLAN_CACHE_KEY = 'selfServiceOnboardingPlansCache';

const readPlanCache = () => {
    try {
        const rawValue = localStorage.getItem(SELF_SERVICE_PLAN_CACHE_KEY);
        return rawValue ? JSON.parse(rawValue) : {};
    } catch (error) {
        return {};
    }
};

const writePlanCache = (journeyType, plans) => {
    try {
        const currentCache = readPlanCache();
        currentCache[journeyType] = Array.isArray(plans) ? plans : [];
        localStorage.setItem(SELF_SERVICE_PLAN_CACHE_KEY, JSON.stringify(currentCache));
    } catch (error) {
        // Ignore cache write failures and keep the UI working.
    }
};

export const fetchSelfServiceCountries = createAsyncThunk("selfService/fetchCountries", async () => {
    try {
        const response = await api({
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.getCountryList,
        });
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const fetchSelfServiceCurrencies = createAsyncThunk("selfService/fetchCurrencies", async () => {
    try {
        const response = await api({
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.getCurrencyList,
        });
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const fetchCountryAssignments = createAsyncThunk("selfService/fetchCountryAssignments", async (companyId) => {
    try {
        const response = await api({
            method: METHOD_TYPE.get,
            url: `${API_ENDPOINTS.getOomeroCompanyCountryAssignments}/${companyId}/country-assignments`,
        });
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const createCountryAssignment = createAsyncThunk("selfService/createCountryAssignment", async ({ companyId, payload }) => {
    try {
        const response = await api({
            method: METHOD_TYPE.post,
            url: `${API_ENDPOINTS.createOomeroCompanyCountryAssignment}/${companyId}/country-assignments`,
            data: payload,
        });
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const updateCountryAssignment = createAsyncThunk("selfService/updateCountryAssignment", async ({ assignmentId, payload }) => {
    try {
        const response = await api({
            method: METHOD_TYPE.put,
            url: `${API_ENDPOINTS.updateOomeroCompanyCountryAssignment}/${assignmentId}`,
            data: payload,
        });
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const fetchTaxProfiles = createAsyncThunk("selfService/fetchTaxProfiles", async (countryId = '') => {
    try {
        const query = countryId ? `?countryId=${countryId}` : '';
        const response = await api({
            method: METHOD_TYPE.get,
            url: `${API_ENDPOINTS.getSelfServiceTaxProfiles}${query}`,
        });
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const createTaxProfile = createAsyncThunk("selfService/createTaxProfile", async (payload) => {
    try {
        const response = await api({
            method: METHOD_TYPE.post,
            url: API_ENDPOINTS.createSelfServiceTaxProfile,
            data: payload,
        });
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const fetchFxRates = createAsyncThunk("selfService/fetchFxRates", async (baseCurrency = '') => {
    try {
        const query = baseCurrency ? `?baseCurrency=${baseCurrency}` : '';
        const response = await api({
            method: METHOD_TYPE.get,
            url: `${API_ENDPOINTS.getSelfServiceFxRates}${query}`,
        });
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const createFxRate = createAsyncThunk("selfService/createFxRate", async (payload) => {
    try {
        const response = await api({
            method: METHOD_TYPE.post,
            url: API_ENDPOINTS.createSelfServiceFxRate,
            data: payload,
        });
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const fetchPricingSlabs = createAsyncThunk("selfService/fetchPricingSlabs", async (countryId = '') => {
    try {
        const query = countryId ? `?countryId=${encodeURIComponent(countryId)}` : '';
        const response = await api({
            method: METHOD_TYPE.get,
            url: `${API_ENDPOINTS.getSelfServicePricingSlabs}${query}`,
        });
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const createPricingSlab = createAsyncThunk("selfService/createPricingSlab", async (payload) => {
    try {
        const response = await api({
            method: METHOD_TYPE.post,
            url: API_ENDPOINTS.createSelfServicePricingSlab,
            data: payload,
        });
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const updatePricingSlab = createAsyncThunk("selfService/updatePricingSlab", async ({ slabId, payload }) => {
    try {
        const response = await api({
            method: METHOD_TYPE.put,
            url: `${API_ENDPOINTS.updateSelfServicePricingSlab}/${slabId}`,
            data: payload,
        });
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const fetchServiceCatalog = createAsyncThunk("selfService/fetchServiceCatalog", async () => {
    try {
        const response = await api({
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.getSelfServiceCatalog,
        });
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const fetchPlan = createAsyncThunk("selfService/fetchPlan", async (entityId) => {
    try {
        const response = await api({
            method: METHOD_TYPE.get,
            url: `${API_ENDPOINTS.getSelfServicePlan}/${entityId}`,
        });
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const quotePreviewPlan = createAsyncThunk("selfService/quotePreviewPlan", async (payload) => {
    try {
        const response = await api({
            method: METHOD_TYPE.post,
            url: API_ENDPOINTS.quotePreviewSelfServicePlan,
            data: payload,
        });
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const confirmPlan = createAsyncThunk("selfService/confirmPlan", async (payload) => {
    try {
        const response = await api({
            method: METHOD_TYPE.post,
            url: API_ENDPOINTS.confirmSelfServicePlan,
            data: payload,
        });
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const updatePlan = createAsyncThunk("selfService/updatePlan", async ({ entityId, payload }) => {
    try {
        const response = await api({
            method: METHOD_TYPE.put,
            url: `${API_ENDPOINTS.updateSelfServicePlan}/${entityId}`,
            data: payload,
        });
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const fetchFormTemplates = createAsyncThunk("selfService/fetchFormTemplates", async (journeyType) => {
    try {
        const response = await api({
            method: METHOD_TYPE.get,
            url: `${API_ENDPOINTS.getSelfServiceFormTemplates}?journeyType=${journeyType}`,
        });
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const createFormTemplate = createAsyncThunk("selfService/createFormTemplate", async (payload) => {
    try {
        const response = await api({
            method: METHOD_TYPE.post,
            url: API_ENDPOINTS.updateSelfServiceFormTemplate,
            data: payload,
        });
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const updateFormTemplate = createAsyncThunk("selfService/updateFormTemplate", async ({ templateId, payload }) => {
    try {
        const response = await api({
            method: METHOD_TYPE.put,
            url: `${API_ENDPOINTS.updateSelfServiceFormTemplate}/${templateId}`,
            data: payload,
        });
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const fetchOnboardingPlans = createAsyncThunk("selfService/fetchOnboardingPlans", async (journeyType = 'individual') => {
    try {
        const response = await api({
            method: METHOD_TYPE.get,
            url: `${API_ENDPOINTS.getOnboardingPlans}?journeyType=${journeyType}`,
        });
        return {
            ...response.data,
            requestedJourneyType: journeyType,
        };
    } catch (error) {
        throw error?.response?.data;
    }
});

export const createOnboardingPlan = createAsyncThunk("selfService/createOnboardingPlan", async (payload) => {
    try {
        const response = await api({
            method: METHOD_TYPE.post,
            url: API_ENDPOINTS.createOnboardingPlan,
            data: payload,
        });
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const fetchOnboardingPlanById = createAsyncThunk("selfService/fetchOnboardingPlanById", async (planId) => {
    try {
        const response = await api({
            method: METHOD_TYPE.get,
            url: `${API_ENDPOINTS.getOnboardingPlans}/${planId}`,
        });
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const updateOnboardingPlan = createAsyncThunk("selfService/updateOnboardingPlan", async ({ planId, payload }) => {
    try {
        const response = await api({
            method: METHOD_TYPE.put,
            url: `${API_ENDPOINTS.updateOnboardingPlan}/${planId}`,
            data: payload,
        });
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

export const deleteOnboardingPlan = createAsyncThunk("selfService/deleteOnboardingPlan", async ({ planId, comment = 'Deleted from self service UI' }) => {
    try {
        const response = await api({
            method: METHOD_TYPE.delete,
            url: `${API_ENDPOINTS.deleteOnboardingPlan}/${planId}?comment=${encodeURIComponent(comment)}`,
        });
        return response.data;
    } catch (error) {
        throw error?.response?.data;
    }
});

const getRows = (payload) => {
    if (Array.isArray(payload?.data?.data)) return payload.data.data;
    if (Array.isArray(payload?.data?.data?.rows)) return payload.data.data.rows;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.data?.rows)) return payload.data.rows;
    if (Array.isArray(payload?.rows)) return payload.rows;
    return [];
};

const selfServiceSlice = createSlice({
    name: "selfService",
    initialState,
    reducers: {
        clearSelfServiceMessage(state) {
            state.lastSuccessMessage = '';
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSelfServiceCountries.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.countryList = payload?.data || [];
                state.error = null;
            })
            .addCase(fetchSelfServiceCurrencies.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.currencyList = payload?.data || [];
                state.error = null;
            })
            .addCase(fetchCountryAssignments.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.countryAssignments = getRows(payload);
                state.error = null;
            })
            .addCase(fetchTaxProfiles.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.taxProfiles = getRows(payload);
                state.error = null;
            })
            .addCase(fetchFxRates.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.fxRates = getRows(payload);
                state.error = null;
            })
            .addCase(fetchPricingSlabs.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.pricingSlabs = getRows(payload);
                state.error = null;
            })
            .addCase(fetchServiceCatalog.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.serviceCatalog = getRows(payload);
                state.error = null;
            })
            .addCase(fetchPlan.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.plan = payload?.data || null;
                state.error = null;
            })
            .addCase(quotePreviewPlan.fulfilled, (state, { payload }) => {
                state.saveLoading = false;
                state.quotePreview = payload?.data || null;
                state.plan = payload?.data || state.plan;
                state.lastSuccessMessage = payload?.message || 'Quote preview generated successfully.';
                state.error = null;
            })
            .addCase(confirmPlan.fulfilled, (state, { payload }) => {
                state.saveLoading = false;
                state.plan = payload?.data || state.plan;
                state.lastSuccessMessage = payload?.message || 'Plan confirmed successfully.';
                state.error = null;
            })
            .addCase(fetchFormTemplates.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.formTemplates = getRows(payload);
                state.error = null;
            })
            .addCase(fetchOnboardingPlans.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                const plans = getRows(payload);
                state.onboardingPlans = plans;
                writePlanCache(payload?.journeyType || payload?.requestedJourneyType || payload?.meta?.journeyType || 'individual', plans);
                state.error = null;
            })
            .addCase(fetchOnboardingPlanById.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.currentOnboardingPlan = payload?.data || null;
                state.error = null;
            })
            .addMatcher(isAnyOf(
                createCountryAssignment.fulfilled,
                updateCountryAssignment.fulfilled,
                createTaxProfile.fulfilled,
                createFxRate.fulfilled,
                createPricingSlab.fulfilled,
                updatePricingSlab.fulfilled,
                updatePlan.fulfilled,
                createFormTemplate.fulfilled,
                updateFormTemplate.fulfilled,
                createOnboardingPlan.fulfilled,
                updateOnboardingPlan.fulfilled,
                deleteOnboardingPlan.fulfilled
            ), (state, { payload }) => {
                state.saveLoading = false;
                state.lastSuccessMessage = payload?.message || 'Saved successfully.';
                state.error = null;
            })
            .addMatcher(isAnyOf(
                fetchSelfServiceCountries.pending,
                fetchSelfServiceCurrencies.pending,
                fetchCountryAssignments.pending,
                fetchTaxProfiles.pending,
                fetchFxRates.pending,
                fetchPricingSlabs.pending,
                fetchServiceCatalog.pending,
                fetchPlan.pending,
                fetchFormTemplates.pending,
                fetchOnboardingPlans.pending,
                fetchOnboardingPlanById.pending
            ), (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addMatcher(isAnyOf(
                createCountryAssignment.pending,
                updateCountryAssignment.pending,
                createTaxProfile.pending,
                createFxRate.pending,
                createPricingSlab.pending,
                updatePricingSlab.pending,
                quotePreviewPlan.pending,
                confirmPlan.pending,
                updatePlan.pending,
                createFormTemplate.pending,
                updateFormTemplate.pending,
                createOnboardingPlan.pending,
                updateOnboardingPlan.pending,
                deleteOnboardingPlan.pending
            ), (state) => {
                state.saveLoading = true;
                state.error = null;
            })
            .addMatcher(isAnyOf(
                fetchSelfServiceCountries.rejected,
                fetchSelfServiceCurrencies.rejected,
                fetchCountryAssignments.rejected,
                fetchTaxProfiles.rejected,
                fetchFxRates.rejected,
                fetchPricingSlabs.rejected,
                fetchServiceCatalog.rejected,
                fetchPlan.rejected,
                fetchFormTemplates.rejected,
                fetchOnboardingPlans.rejected,
                fetchOnboardingPlanById.rejected,
                createCountryAssignment.rejected,
                updateCountryAssignment.rejected,
                createTaxProfile.rejected,
                createFxRate.rejected,
                createPricingSlab.rejected,
                updatePricingSlab.rejected,
                quotePreviewPlan.rejected,
                confirmPlan.rejected,
                updatePlan.rejected,
                createFormTemplate.rejected,
                updateFormTemplate.rejected,
                createOnboardingPlan.rejected,
                updateOnboardingPlan.rejected,
                deleteOnboardingPlan.rejected
            ), (state, { error }) => {
                state.isLoading = false;
                state.saveLoading = false;
                state.error = error?.message || "Request Failed Please Try Again";
            });
    }
});

export const { clearSelfServiceMessage } = selfServiceSlice.actions;
export default selfServiceSlice.reducer;
